// Platform icons shared by Competencia (and the upcoming Marketing module).
// lucide-react deprecated its brand icons, so the brands are small inline
// SVGs drawn on lucide's 24px grid (2px stroke) to sit next to lucide icons.
import { Ellipsis, Globe } from "lucide-react";
import { useTranslate } from "ra-core";
import type { ReactNode, SVGProps } from "react";

import { cn } from "@/lib/utils";

import { isPlatform, platformLabelKey } from "./platforms";
import type { SocialPlatform } from "./types";

type GlyphProps = SVGProps<SVGSVGElement>;

const Svg = ({ children, ...props }: GlyphProps & { children: ReactNode }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    focusable="false"
    {...props}
  >
    {children}
  </svg>
);

const GLYPHS: Record<SocialPlatform, (props: GlyphProps) => ReactNode> = {
  x: (props) => (
    <Svg {...props}>
      <path d="M4 4l11.7 16H20L8.3 4z" />
      <path d="M4 20l6.8-6.8M13.2 10.8L20 4" />
    </Svg>
  ),
  instagram: (props) => (
    <Svg {...props}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <path d="M17.5 6.5h.01" />
    </Svg>
  ),
  youtube: (props) => (
    <Svg {...props}>
      <rect x="2" y="5" width="20" height="14" rx="4" />
      <path d="M10 9l5 3-5 3z" />
    </Svg>
  ),
  tiktok: (props) => (
    <Svg {...props}>
      <path d="M21 7.9V12a10 10 0 0 1-5-2v4.5a6.5 6.5 0 1 1-8-6.3v4.3a2.5 2.5 0 1 0 4 2V3h4.1A6 6 0 0 0 21 7.9z" />
    </Svg>
  ),
  linkedin: (props) => (
    <Svg {...props}>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 11v5M8 8v.01M12 16v-5M16 16v-3a2 2 0 0 0-4 0" />
    </Svg>
  ),
  facebook: (props) => (
    <Svg {...props}>
      <path d="M7 10v4h3v7h4v-7h3l1-4h-4V8a1 1 0 0 1 1-1h3V3h-3a5 5 0 0 0-5 5v2z" />
    </Svg>
  ),
  web: (props) => <Globe aria-hidden {...props} />,
  otro: (props) => <Ellipsis aria-hidden {...props} />,
};

/** Tinted tile colours per brand, readable in light and dark mode. */
const TONE: Record<SocialPlatform, string> = {
  x: "bg-neutral-900/8 text-neutral-900 dark:bg-white/12 dark:text-white",
  instagram:
    "bg-pink-500/12 text-pink-700 dark:bg-pink-400/15 dark:text-pink-300",
  youtube: "bg-red-500/12 text-red-700 dark:bg-red-400/15 dark:text-red-300",
  tiktok: "bg-cyan-500/12 text-cyan-800 dark:bg-cyan-400/15 dark:text-cyan-200",
  linkedin: "bg-sky-600/12 text-sky-800 dark:bg-sky-400/15 dark:text-sky-200",
  facebook:
    "bg-blue-600/12 text-blue-800 dark:bg-blue-400/15 dark:text-blue-200",
  web: "bg-primary/10 text-primary dark:bg-primary/20",
  otro: "bg-muted text-muted-foreground",
};

/** The bare platform glyph (decorative; pair it with a label). */
export const PlatformIcon = ({
  platform,
  className,
}: {
  platform: SocialPlatform;
  className?: string;
}) => {
  const Glyph = GLYPHS[isPlatform(platform) ? platform : "otro"];
  return <Glyph className={cn("size-4 shrink-0", className)} />;
};

/**
 * The glyph in a tinted round tile. The platform name is the accessible text
 * (visible with `showLabel`, screen-reader only otherwise).
 */
export const PlatformBadge = ({
  platform,
  showLabel,
  size = "md",
  className,
}: {
  platform: SocialPlatform;
  showLabel?: boolean;
  size?: "sm" | "md";
  className?: string;
}) => {
  const translate = useTranslate();
  const known = isPlatform(platform) ? platform : "otro";
  const label = translate(platformLabelKey(known));
  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1.5", className)}
      data-platform={known}
    >
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full",
          size === "sm" ? "size-6" : "size-8",
          TONE[known],
        )}
      >
        <PlatformIcon
          platform={known}
          className={size === "sm" ? "size-3.5" : "size-4"}
        />
      </span>
      {showLabel ? (
        <span className="truncate text-sm font-medium">{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </span>
  );
};
